import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getDrainSettings, updateDrainSettings } from "@/lib/drain-settings.functions";

export const Route = createFileRoute("/_authenticated/drain-settings")({
  component: DrainSettingsPage,
  head: () => ({ meta: [{ title: "Drain Settings — InboxPulse" }] }),
});

function DrainSettingsPage() {
  const fetchSettings = useServerFn(getDrainSettings);
  const saveSettings = useServerFn(updateDrainSettings);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["drain-settings"],
    queryFn: () => fetchSettings(),
  });

  const [form, setForm] = useState({
    batch_size: 500,
    lock_ttl_seconds: 120,
    max_attempts: 5,
    cron_interval_minutes: 1,
  });

  useEffect(() => {
    if (data) {
      setForm({
        batch_size: data.batch_size,
        lock_ttl_seconds: data.lock_ttl_seconds,
        max_attempts: data.max_attempts,
        cron_interval_minutes: data.cron_interval_minutes,
      });
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: typeof form) => saveSettings({ data: input }),
    onSuccess: () => {
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["drain-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-8 text-destructive">{(error as Error).message}</div>;
  if (!data) return null;

  const fields: Array<{
    key: keyof typeof form;
    label: string;
    hint: string;
    envName: string;
    overridden: boolean;
  }> = [
    {
      key: "batch_size",
      label: "Batch size",
      hint: "Rows claimed per drain run (1–10000).",
      envName: "DRAIN_BATCH_SIZE",
      overridden: data.envOverrides.batch_size,
    },
    {
      key: "lock_ttl_seconds",
      label: "Lock TTL (seconds)",
      hint: "How long a claimed row stays locked before being re-claimable (1–3600).",
      envName: "DRAIN_LOCK_TTL_SECONDS",
      overridden: data.envOverrides.lock_ttl_seconds,
    },
    {
      key: "max_attempts",
      label: "Max attempts",
      hint: "Attempts before a queue row is dead-lettered (1–100).",
      envName: "DRAIN_MAX_ATTEMPTS",
      overridden: data.envOverrides.max_attempts,
    },
    {
      key: "cron_interval_minutes",
      label: "Cron interval (minutes)",
      hint: "Informational. Update pg_cron schedule separately to apply.",
      envName: "DRAIN_CRON_INTERVAL_MINUTES",
      overridden: data.envOverrides.cron_interval_minutes,
    },
  ];

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <PageHeader
        title="Drain Settings"
        description="Tune the tracking queue drain worker. Environment variables override stored values at runtime."
      />
      <HelpPanel
        title="Hilfe: Hintergrund-Verarbeitung einstellen (nur für Profis)"
        what={
          <>
            Hier kannst du <b>tief im Maschinenraum</b> einstellen, wie schnell und wie groß die
            Worker die Pixel-Treffer abarbeiten. <b>Im Normalfall nichts anfassen.</b>
          </>
        }
        why={
          <>
            Wenn die Warteschlange ständig zu voll wird, kann man die Batch-Größe oder die
            Geschwindigkeit erhöhen. Falsche Werte können aber das System überlasten.
          </>
        }
        how={[
          <>Werte nur ändern, wenn du weißt was du tust — oder Rücksprache mit Admin.</>,
          <>
            Nach Änderung 5-10 Min beobachten unter <b>Queue health</b>.
          </>,
          <>Bei Problemen: auf Standard-Werte zurücksetzen.</>,
        ]}
        tips={[<>Im Zweifel: Finger weg. Das System läuft mit Standard-Werten sehr stabil.</>]}
        glossary={[
          { term: "Drain", def: "Das Abarbeiten der Warteschlange (wörtlich: ablassen)." },
          { term: "Worker", def: "Hintergrund-Programm, das eine Aufgabe abarbeitet." },
          { term: "Batch-Größe", def: "Wieviele Treffer auf einmal verarbeitet werden." },
        ]}
      />
      <Card className="p-6 bg-card/60 border-border space-y-5">
        {fields.map((f) => (
          <div key={f.key} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={f.key}>{f.label}</Label>
              {f.overridden && (
                <Badge variant="secondary" title={`Overridden by env var ${f.envName}`}>
                  env override
                </Badge>
              )}
            </div>
            <Input
              id={f.key}
              type="number"
              value={form[f.key]}
              onChange={(e) =>
                setForm((p) => ({ ...p, [f.key]: Number.parseInt(e.target.value, 10) || 0 }))
              }
              disabled={f.overridden}
            />
            <p className="text-xs text-muted-foreground">{f.hint}</p>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(data.updated_at).toLocaleString()}
          </p>
          <Button
            onClick={() => mutation.mutate(form)}
            disabled={mutation.isPending}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
